import config from 'config'
import { ON_REFRESH, ON_LOGIN, PLAY, ON_PLAY_RESPONSE, SET_PLAYING, SET_FORCE_UPDATE,
  SHOW_MESSAGE, UPDATE_WIN, SET_MUTE, SET_SETTINGSSCREEN, SET_SKIPFEATURE, SET_PAUSED,
  SET_SPACE_TO_SPIN, SET_LOADING, UPDATE_PROGRESS, HIDE_LOADER, SET_SETTING,
  DISPLAY_FREEROUND, SET_ORIENTATION_CHECKED, UPDATE_FREEROUNDS_LEFT, SET_MUTE_SFX,
  SET_MUTE_MUSIC, RESTORE_SETTINGS, TOGGLE_SETTING, SET_LOGGEDIN, SET_SOUND_AVAILABLE,
  LOAD_SOUNDS, SOUNDS_LOADED, SET_STEP_DEBUG, SET_QUICKSPIN, SET_ALLOW_SPIN_REQUESTS,
  SET_INFOCUS, ADD_ENABLE_QUICKSPIN, SET_QUICKSPIN_PROMPT_SHOWN, TOGGLE_FPSMETER,
  DISABLE_RESTORESTATE, TOGGLE_HOTZONE, SET_CHEAT, SET_CHEATS, SET_DEMO, SET_DEMO_INDEX, SET_GAMESPEED,
  SET_FALLBACK_RESPONSE, SET_HASSPLASH, SET_HIDE_SPLASH, CURRENCY_CHANGED,
  SET_HASINTRO, SET_HIDE_INTRO, SET_INTRO_LOADED, SET_DELAYING_SPIN, SET_SIDE_GAMES, SET_SIDE_BET,
  PLAY_DONE, TOGGLE_RECORDING_CONTROLS, TOGGLE_CONFIGURABLE_VARIABLES, SET_WAITING_FOR_CONNECTION,
  SET_MENU_OPACITY, TOGGLE_POPUP_TRIGGERER, CALLED_GAMEFINISHED,
  // POSTMESSAGE
  SET_EXTERNAL_PAUSE, SET_EXTERNAL_PAUSE_REQUEST, GAME_IS_READY,
  SET_EXTERNAL_ERROR_HANDLING,
} from '../constants'
import createReducer from '../createReducer'
import getInitialState from '../initialStates/base'
import updateMessage from './updateMessages'

/**
 * Creates a new reducer, and also updates the paused state of the game.
 * @param {object} state - The current state of the client.
 * @param {object} diff - The updates to the current state.
 */
const newReducer = (state, diff) => {
  const tmp = createReducer(state, diff)

  tmp.paused = (tmp._paused || (tmp.messages && !!tmp.messages.length) ||
    !!tmp.settingScreen || !tmp.inFocus || (tmp.hasSplash && !tmp.hideSplash) ||
    (tmp.hasIntro && !tmp.hideIntro) || tmp.externalPause)

  if (diff.win && !diff.dropWinCount) {
    tmp.dropWinCount = false
  }

  return tmp
}

/**
 * Reducer that handles the different action events from the MacGuffin framework.
 * @param {object} state - The current state.
 * @param {object} action - The action which is provided, containing event information.
 * @returns {{win: number, muted: boolean, mutedSFX: boolean, mutedMusic: boolean, settingScreen: boolean, orientationChecked: boolean, swipePlea: boolean, assetProgress: {assets: number}, hideLoader: boolean, messages: Array, stepDebug: boolean, enableQuickspinCounter: number, quickSpinPromptShown: boolean, showQuickSpinPrompt: boolean, allowSpinRequests: boolean, _paused: boolean, paused: boolean, inFocus: boolean, restoreStateDisabled: boolean, hotzones: {ios: boolean, samsung: boolean}, settings: {}}}
 */
export default function baseReducer(state = getInitialState(), action) {
  switch (action.type) {
    case SET_LOADING: {
      const { loading } = action
      return newReducer(state, { loading })
    }
    case UPDATE_PROGRESS: {
      const { progress, asset } = action

      const assetProgress = { ...state.assetProgress }

      assetProgress[asset] = progress

      return newReducer(state, { assetProgress })
    }
    case SET_INFOCUS: {
      const { inFocus } = action
      return newReducer(state, { inFocus })
    }
    case ON_LOGIN: {
      const { response } = action
      return newReducer(state, { response })
    }
    case PLAY: {
      if (!state.allowSpinRequests) {
        return state
      }
      const { params } = action
      const pickMode = params.ga === 'makePick'
      const newState = {
        playPending: params.ga,
        playing: true,
        didCallGameFinished: false,
        win: (pickMode ? state.win : 0)
      }

      if (!params.skipSkip) {
        newState.skipfeature = false
      }

      if (params.timestamp)
        newState.playStart = params.timestamp

      return newReducer(state, newState)
    }
    case ON_PLAY_RESPONSE: {
      const { response } = action

      if (response && response.errorCode)
        return { ...state }

      return newReducer(state, { playPending: false, response })
    }
    case PLAY_DONE: {
      return newReducer(state, { playing: false })
    }
    case SET_PLAYING: {
      const { playing } = action
      return newReducer(state, { playing })
    }
    case ON_REFRESH: {
      const { config, response } = action
      return newReducer(state, { config, response, fallbackResponse: response })
    }
    case SHOW_MESSAGE: {
      const { message } = action
      const { messages } = state
      let newMessages = [ ...messages ]
      if (!message) {
        newMessages.shift()
      } else {
        newMessages = updateMessage({ message, newMessages })
      }

      return newReducer(state, { messages: newMessages })
    }
    case CALLED_GAMEFINISHED: {
      return newReducer(state, { didCallGameFinished: true })
    }
    case SET_EXTERNAL_ERROR_HANDLING: {
      const { externalErrorHandling } = action
      return newReducer(state, { externalErrorHandling })
    }
    case DISPLAY_FREEROUND: {
      const { display, isTournament } = action
      return newReducer(state, { displayFreeRound: display, isTournament })
    }
    case UPDATE_WIN: {
      const { win, dropWinCount } = action
      return newReducer(state, { win, dropWinCount })
    }
    case SET_FORCE_UPDATE: {
      const { forceUpdate } = action
      return newReducer(state, { forceUpdate })
    }
    case SET_MUTE: {
      return newReducer(state, { muted: action.mute })
    }
    case SET_MUTE_SFX: {
      return newReducer(state, { mutedSFX: action.mute })
    }
    case SET_MUTE_MUSIC: {
      return newReducer(state, { mutedMusic: action.mute })
    }
    case SET_SPACE_TO_SPIN: {
      return newReducer(state, { spaceToSpin: action.spaceToSpin })
    }
    case SET_SETTINGSSCREEN: {
      return newReducer(state, { settingScreen: action.screen })
    }
    case SET_PAUSED: {
      return newReducer(state, { _paused: action.paused })
    }
    case SET_SKIPFEATURE: {
      const { skipfeature } = action
      return newReducer(state, { skipfeature: skipfeature && !config.disableSkipFeature && !state.delayingSpin })
    }
    case SET_ORIENTATION_CHECKED: {
      const { checked } = action
      return newReducer(state, { orientationChecked: checked })
    }
    case UPDATE_FREEROUNDS_LEFT: {
      const { spins } = action
      return newReducer(state, { freeRoundSpins: spins })
    }
    case HIDE_LOADER: {
      return newReducer(state, { hideLoader: true })
    }
    case RESTORE_SETTINGS: {
      const { settings } = action
      return newReducer(state, { settings })
    }
    case SET_SETTING: {
      const { setting, value } = action

      const settings = { ...state.settings }
      
      settings[setting] = value

      return newReducer(state, { settings })
    }
    case TOGGLE_SETTING: {
      const { setting } = action

      const settings = { ...state.settings }

      settings[setting] = !state.settings[setting]

      return newReducer(state, { settings })
    }
    case SET_HASSPLASH: {
      const { hasSplash } = action
      return newReducer(state, { hasSplash })
    }
    case SET_HIDE_SPLASH: {
      const { hideSplash } = action
      return newReducer(state, { hideSplash })
    }
    case SET_HASINTRO: {
      const { hasIntro } = action
      return newReducer(state, { hasIntro, loading: true })
    }
    case SET_HIDE_INTRO : {
      const { hideIntro } = action
      return newReducer(state, { hideIntro })
    }
    case SET_INTRO_LOADED: {
      const { introLoaded } = action
      return newReducer(state, { introLoaded })
    }
    case SET_LOGGEDIN: {
      const { loggedIn } = action
      return newReducer(state, { loggedIn })
    }
    case SET_SOUND_AVAILABLE: {
      const { soundAvailable } = action
      return newReducer(state, { soundAvailable })
    }
    case LOAD_SOUNDS: {
      return newReducer(state, { loadingSounds: true })
    }
    case SOUNDS_LOADED: {
      return newReducer(state, { soundsLoaded: true, loadingSounds: false })
    }
    case SET_DELAYING_SPIN: {
      const { delay } = action
      return newReducer(state, { delayingSpin: delay })
    }
    case SET_SIDE_BET: {
      const { betAmount, sideGameName } = action
      const { sideGames } = state
      const newSideGames = [ ...sideGames ]

      const sideGame = sideGames.find(a => a.sideGameName === sideGameName)
      if (sideGame) {
        sideGame.betAmount = betAmount
      }
      return newReducer(state, { sideGames: newSideGames })
    }
    case SET_SIDE_GAMES: {
      const { sideGames } = action

      let array = []
      array = Object.values(sideGames)
      const keys = Object.keys(sideGames)
      for (let i = 0; i < array.length; i++) {
        array[i].betAmount = 0
        array[i].sideGameName = keys[i]
      }

      return newReducer(state, { sideGames: array })
    }
    case SET_STEP_DEBUG: {
      const { stepDebug = false } = action
      return newReducer(state, { stepDebug })
    }
    case SET_QUICKSPIN: {
      const { quickSpin } = action
      const { settings } = state
      const newSettings = { ...settings, quickSpin }

      return newReducer(state, { settings: newSettings })
    }
    case SET_ALLOW_SPIN_REQUESTS: {
      const { allowSpinRequests } = action
      return newReducer(state, { allowSpinRequests })
    }
    case ADD_ENABLE_QUICKSPIN: {
      const { add = 0 } = action
      const { enableQuickspinCounter } = state
      return newReducer(state, { enableQuickspinCounter: (enableQuickspinCounter + add) })
    }
    case SET_QUICKSPIN_PROMPT_SHOWN: {
      const { shown = false } = action
      return newReducer(state, { quickSpinPromptShown: shown })
    }
    case DISABLE_RESTORESTATE: {
      const { disable } = action
      return newReducer(state, { restoreStateDisabled: disable })
    }
    case TOGGLE_HOTZONE: {
      const { hotzone } = action
      const hotzones = state.hotzones
      const newHotzone = {}
      newHotzone[hotzone] = !hotzones[hotzone]

      const newHotzones = {
        ...hotzones,
        ...newHotzone,
      }

      return newReducer(state, { hotzones: newHotzones })
    }
    case TOGGLE_FPSMETER: {
      return newReducer(state, { hideFPSmeter: !state.hideFPSmeter })
    }
    case TOGGLE_RECORDING_CONTROLS: {
      return newReducer(state, { showRecordingControls: !state.showRecordingControls })
    }
    case TOGGLE_CONFIGURABLE_VARIABLES: {
      return newReducer(state, { showConfigurableVariables: !state.showConfigurableVariables })
    }
    case TOGGLE_POPUP_TRIGGERER: {
      return newReducer(state, { showPopupTriggerer: !state.showPopupTriggerer })
    }
    case SET_CHEAT: {
      const { cheat } = action
      return newReducer(state, { serverCheat: cheat })
    }
    case SET_CHEATS: {
      const { cheats } = action
      return newReducer(state, { gameCheats: cheats })
    }
    case SET_DEMO: {
      const { demo } = action
      return newReducer(state, { demo })
    }
    case SET_DEMO_INDEX: {
      const { index } = action
      return newReducer(state, { demoIndex: index } )
    }
    case SET_GAMESPEED: {
      const { gamespeed } = action
      return newReducer(state, { gamespeed })
    }
    case SET_MENU_OPACITY: {
      const { menuOpacity } = action
      return newReducer(state, { menuOpacity })
    }
    case SET_FALLBACK_RESPONSE: {
      const { fallbackResponse } = action
      return newReducer(state, { fallbackResponse })
    }
    case CURRENCY_CHANGED: {
      const { currency } = action
      return newReducer(state, { currencyChanged: currency })
    }
    case SET_EXTERNAL_PAUSE: {
      const { externalPause } = action
      const externalRC = !externalPause ? false : state.externalRC
      return newReducer(state, { externalPause, externalRC })
    }
    case SET_EXTERNAL_PAUSE_REQUEST: {
      const { externalPauseRequest, id = undefined, externalRC = false } = action
      return newReducer(state, { externalPauseRequest, externalPauseRequestId: id, externalRC })
    }
    case GAME_IS_READY: {
      return newReducer(state, { gameIsReady: true })
    }

    case SET_WAITING_FOR_CONNECTION: {
      const { show } = action
      return newReducer(state, { waitingForConnection: show })
    }

    default:
      return state
  }
}
